/* eslint-disable camelcase */
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import { FakeLink } from '@hivebrite/design-system/Link';

import BoxWrapper from 'hb-react/shared/apps/Events/components/EventRegistrationForm/components/BoxWrapper';
import GuestSelect from 'hb-react/shared/apps/Events/components/EventRegistrationForm/components/GuestSelect';
import StandardFields from 'hb-react/shared/apps/Events/components/EventRegistrationForm/components/StandardFields';
import TicketAddons from 'hb-react/shared/apps/Events/components/EventRegistrationForm/components/TicketAddons';
import { useEventContext } from 'hb-react/shared/apps/Events/contexts/EventContext';
import {
  GUEST_OPTIONS_OTHER_VALUE,
  GUEST_OPTIONS_CURRENT_USER_VALUE,
} from 'hb-react/shared/apps/Events/hooks';
import {
  Checkbox,
  CustomAttribute,
  Email,
  Text,
} from 'hb-react/shared/components/formik/components/inputs';

const GuestsTicketForm = ({
  currentUser,
  attendee,
  ticket,
  index,
  guestsOptions,
  deleteGuest,
  updateGuest,
  createGuest,
}) => {
  const { event } = useEventContext();
  const [expanded, setExpanded] = useState(false);

  const {
    setFieldValue,
    values: {
      network_events_booking: { attendees_attributes },
    },
  } = useFormikContext();

  const { customizable_attributes } = ticket;
  const { standard_fields, same_attendee_for_all_tickets_enabled } = event;

  useEffect(() => {
    if (attendee?.guest_id === null && expanded) {
      setExpanded(false);
    } else if (attendee?.guest_id && !expanded) {
      setExpanded(true);
    }
  }, [attendee]);

  const formFields = useMemo(
    () => [
      'email',
      'firstname',
      'lastname',
      ...Object.values(standard_fields || {}).map(({ name }) => name),
      ...Object.values(customizable_attributes || {}).map(({ name }) => name),
    ],
    [standard_fields, customizable_attributes],
  );

  const setFieldValueForUser = useCallback(
    (user, i = index) => {
      formFields.forEach((field) =>
        setFieldValue(
          `network_events_booking[attendees_attributes][${i}][${field}]`,
          user?.[field],
        ),
      );
    },
    [index],
  );

  const clearValue = useCallback(
    (i, resetGuest = false) => {
      if (resetGuest) {
        setFieldValue(
          `network_events_booking[attendees_attributes][${i}][guest_id]`,
          GUEST_OPTIONS_OTHER_VALUE,
        );
      }

      formFields.forEach((field) =>
        setFieldValue(
          `network_events_booking[attendees_attributes][${i}][${field}]`,
          '',
          false,
        ),
      );
    },
    [formFields],
  );

  const onChange = (value) => {
    setExpanded(value !== null);

    if (value?.value === GUEST_OPTIONS_OTHER_VALUE) {
      return clearValue(index);
    }

    setFieldValue(
      `network_events_booking[attendees_attributes][${index}][user_id]`,
      value?.value === GUEST_OPTIONS_CURRENT_USER_VALUE ? currentUser.id : '',
    );

    setFieldValueForUser(value);
  };

  const onUpdate = (payload) => {
    if (attendee.guest_id === GUEST_OPTIONS_CURRENT_USER_VALUE) {
      return;
    }

    if (attendee.guest_id !== GUEST_OPTIONS_OTHER_VALUE) {
      return updateGuest({
        guest: {
          ...attendee,
          ...payload,
        },
      });
    }

    const guest_id = uuid();

    setFieldValue(
      `network_events_booking[attendees_attributes][${index}][guest_id]`,
      guest_id,
    );

    return createGuest({
      guest: { ...attendee, ...payload, guest_id },
    });
  };

  const onDelete = useCallback(
    (guest) => {
      attendees_attributes
        .map((attendee, i) => (attendee.guest_id === guest.guest_id ? i : ''))
        .filter(String)
        .forEach((ticketIndex) => clearValue(ticketIndex, true));

      deleteGuest({ guest });
    },
    [attendees_attributes],
  );

  const onPrefill = useCallback(() => {
    attendees_attributes.forEach((_, i) => {
      if (i === index) {
        return;
      }

      setFieldValue(
        `network_events_booking[attendees_attributes][${i}][guest_id]`,
        attendee.guest_id,
      );

      setFieldValue(
        `network_events_booking[attendees_attributes][${i}][user_id]`,
        attendee.user_id,
      );

      setFieldValueForUser(attendee, i);
    });
  }, [attendees_attributes]);

  return (
    <Card bg="white" data-testid="event-ticket-guest" mb={5} pt={4} pb={3}>
      <Box font="h3-semibold" mb={3}>
        {__('Ticket %{index} • ').i__({ index: index + 1 })}
        {ticket.name}
      </Box>
      <Hr />
      <Box mb={6} flexDirection="column" maxWidth="536px">
        <GuestSelect
          name={`network_events_booking[attendees_attributes][${index}][guest_id]`}
          undeletable={[
            `${currentUser.firstname} ${currentUser.lastname}`,
            __('Other'),
            __('Add an external guest'),
          ]}
          guestsOptions={guestsOptions}
          onChange={onChange}
          onDelete={onDelete}
          withoutWrapperCSS
        />

        {index === 0 && (
          <Box flexDirection="column">
            {event.show_participants &&
              attendee?.guest_id === GUEST_OPTIONS_CURRENT_USER_VALUE && (
                <Box mr="auto" mt={4}>
                  <Checkbox
                    name={`network_events_booking[attendees_attributes][${index}][hidden]`}
                    label={__('Hide me from the list of participants')}
                    labelMultiline
                    wrapperProps={{ mb: 0 }}
                  />
                </Box>
              )}
            {same_attendee_for_all_tickets_enabled &&
              attendee?.guest_id &&
              attendees_attributes.length > 1 && (
                <Box mt={4}>
                  <FakeLink onClick={onPrefill}>
                    {__('Same attendee for all tickets')}
                  </FakeLink>
                </Box>
              )}
          </Box>
        )}
      </Box>
      {expanded && (
        <>
          <Box
            flexDirection={{ _: 'column', m: 'row' }}
            flexWrap="wrap"
            maxWidth="536px"
          >
            <BoxWrapper>
              <Text
                name={`network_events_booking[attendees_attributes][${index}][firstname]`}
                label={__('First name')}
                onChange={(firstname) => onUpdate({ firstname })}
                validate={{
                  required: true,
                }}
              />
            </BoxWrapper>
            <BoxWrapper>
              <Text
                name={`network_events_booking[attendees_attributes][${index}][lastname]`}
                label={__('Last name')}
                onChange={(lastname) => onUpdate({ lastname })}
                validate={{
                  required: true,
                }}
              />
            </BoxWrapper>

            <BoxWrapper>
              <Email
                name={`network_events_booking[attendees_attributes][${index}][email]`}
                label={__('Email address')}
                onChange={(email) => onUpdate({ email })}
                validate={{
                  required: true,
                }}
              />
            </BoxWrapper>

            {standard_fields && (
              <StandardFields
                fields={standard_fields}
                name={`network_events_booking[attendees_attributes][${index}]`}
                onUpdate={onUpdate}
                Wrapper={BoxWrapper}
              />
            )}

            {customizable_attributes &&
              customizable_attributes.length > 0 &&
              customizable_attributes.map((attribute) => (
                <BoxWrapper
                  key={attribute.id}
                  $fullWidth={attribute.attribute_type === 'double_select'}
                >
                  <CustomAttribute
                    name={`network_events_booking[attendees_attributes][${index}][${attribute.name}]`}
                    customAttribute={attribute}
                    labelMultiline
                    onChange={(v) => {
                      if (attribute.attribute_type === 'select') {
                        return onUpdate({
                          [attribute.name]: Array.isArray(v)
                            ? v.map((v) => v.value)
                            : [v.value],
                        });
                      }

                      return onUpdate({
                        [attribute.name]: v,
                      });
                    }}
                  />
                </BoxWrapper>
              ))}
          </Box>
          <TicketAddons
            addons={attendee.addons_attributes}
            ticketIndex={index}
          />
        </>
      )}
    </Card>
  );
};

GuestsTicketForm.propTypes = {
  ticket: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    maximum_per_person: PropTypes.number.isRequired,
    customizable_attributes: PropTypes.arrayOf(
      PropTypes.shape({
        attribute_type: PropTypes.string.isRequired,
        customizable_type: PropTypes.string.isRequired,
        display_name: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired,
        multi: PropTypes.bool,
        name: PropTypes.string.isRequired,
        options: PropTypes.array,
        placeholder: PropTypes.string,
        required: PropTypes.bool,
        text_size: PropTypes.number,
        tooltip: PropTypes.string.isRequired,
        visibility: PropTypes.string.isRequired,
      }),
    ),
    addons: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        price: PropTypes.string.isRequired,
        description: PropTypes.string,
        currency: PropTypes.shape({
          code: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          symbol: PropTypes.string.isRequired,
        }).isRequired,
      }),
    ),
  }).isRequired,
};

export default GuestsTicketForm;

const Hr = styled.hr`
  margin-top: 0;
  margin-bottom: ${({ theme }) => theme.space[5]}px;
  border-color: ${({ theme }) => theme.colors.horizon};
`;
